import { useCallback, useEffect, useMemo, useState } from "react";
import { TbArrowLeft } from "react-icons/tb";
import { CurrencyIcon } from "../../../../Components/Atoms/CurrencyIcon";
import { useToast } from "../../../../Hooks/toast";
import { useProgressAlert } from "../../../../Hooks/useProgressAlert";
import {
  createIbOutboundTransfer,
  formatBaasErrorMessage,
} from "../../../../Services/ibContaInternacionalOpsApi";
import { useContaInternacionalBalances } from "../../../../Hooks/useContaInternacionalBalances";
import { useContaInternacionalCurrencies } from "../../../../Hooks/useContaInternacionalCurrencies";
import { CurrencySelect } from "./CurrencySelect";
import { ContaInternacionalTransferStepper } from "./ContaInternacionalTransferStepper";
import {
  DEFAULT_CURRENCY_ID,
  formatCurrencyAmount,
  formatPercent,
  getCurrency,
  getTransferBeneficiaryFields,
  getTransferBeneficiarySummaryLines,
  getTransferQuote,
  JURISDICTION,
  TRANSFER_WIZARD_STEPS,
} from "../constants";
import { getCurrencyRails } from "../../../../Services/ibAccountCurrenciesApi";

const EMPTY_BENEFICIARY_FIELDS = {};
const FINAL_STEP_INDEX = TRANSFER_WIZARD_STEPS.length - 1;

const renderBeneficiaryField = (field, beneficiaryFields, onChange) => (
  <div key={field.key} className="space-y-1">
    <label
      htmlFor={`transfer-${field.key}`}
      className="block text-xs font-medium text-gray-500"
    >
      {field.label}
      {!field.required && <span className="font-normal text-gray-400"> (opcional)</span>}
    </label>
    {field.inputType === "select" ? (
      <select
        id={`transfer-${field.key}`}
        value={beneficiaryFields[field.key] ?? ""}
        onChange={(e) => onChange(field.key, e.target.value)}
        className="valut-input h-10 w-full rounded-xl"
      >
        <option value="">{field.placeholder}</option>
        {field.options?.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    ) : (
      <input
        id={`transfer-${field.key}`}
        type="text"
        placeholder={field.placeholder}
        value={beneficiaryFields[field.key] ?? ""}
        onChange={(e) => onChange(field.key, e.target.value)}
        className="valut-input h-10 w-full rounded-xl"
      />
    )}
  </div>
);

const ContaInternacionalTransferSummary = ({
  currency,
  selectedMethod,
  beneficiarySummaryLines,
  transferQuote,
  numericAmount,
  balanceAfter,
  showActionButton = false,
  onSubmit,
}) => (
  <section className="rounded-xl border border-gray-100 bg-gray-50/50 p-3">
    <h3 className="text-sm font-bold text-brand-dark">Resumo</h3>
    <p className="mt-0.5 text-xs text-gray-500">
      Transferência externa a partir da conta em {currency.code} ({JURISDICTION.countryLabel}).
    </p>
    <div className="mt-3 space-y-2 text-sm">
      <div className="flex justify-between">
        <span className="text-gray-500">Moeda de origem</span>
        <span className="font-semibold text-brand-dark">{currency.code}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500">Saldo disponível</span>
        <span className="font-semibold text-brand-dark">
          {formatCurrencyAmount(currency.balance, currency.id)}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500">Meio selecionado</span>
        <span className="font-semibold text-brand-dark">{selectedMethod?.label ?? "—"}</span>
      </div>
      {beneficiarySummaryLines.length > 0 && (
        <div className="space-y-1.5 border-t border-gray-100 pt-2">
          <p className="text-xs font-medium text-gray-500">Beneficiário</p>
          {beneficiarySummaryLines.map((line) => (
            <div key={line.label} className="flex justify-between gap-3">
              <span className="text-gray-500">{line.label}</span>
              <span className="max-w-[55%] truncate text-right font-semibold text-brand-dark">
                {line.value}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between">
        <span className="text-gray-500">Valor</span>
        <span className="font-semibold text-brand-dark">
          {transferQuote.hasAmount ? formatCurrencyAmount(numericAmount, currency.id) : "—"}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500">
          {transferQuote.feePercent != null
            ? `${transferQuote.feeLabel} (${formatPercent(transferQuote.feePercent)})`
            : transferQuote.feeLabel}
        </span>
        <span className="font-semibold text-brand-dark">
          {transferQuote.hasAmount ? formatCurrencyAmount(transferQuote.fee, currency.id) : "—"}
        </span>
      </div>
      <div className="flex justify-between border-t border-gray-100 pt-2">
        <span className="font-semibold text-brand-dark">Total a debitar</span>
        <span className="font-semibold text-brand-dark">
          {transferQuote.hasAmount
            ? formatCurrencyAmount(transferQuote.totalDebit, currency.id)
            : "—"}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500">Saldo após</span>
        <span className="font-semibold text-brand-dark">
          {formatCurrencyAmount(balanceAfter, currency.id)}
        </span>
      </div>
    </div>
    {showActionButton && (
      <button type="button" className="btn-primary mt-3 w-full" onClick={onSubmit}>
        Iniciar transferência
      </button>
    )}
  </section>
);

const TransferWizardFooter = ({
  step,
  onBack,
  onPrimary,
  primaryLabel,
}) => (
  <div className="mt-6 flex flex-col-reverse gap-2 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
    {step > 0 ? (
      <button type="button" className="btn-back w-full sm:w-auto" onClick={onBack}>
        <TbArrowLeft className="h-4 w-4" />
        Voltar
      </button>
    ) : (
      <span className="hidden sm:block" />
    )}
    <button type="button" className="btn-primary w-full sm:w-auto" onClick={onPrimary}>
      {primaryLabel}
    </button>
  </div>
);

export const ContaInternacionalTransferirContent = ({
  isActive = true,
  currencyId,
  hideActionButton = false,
  onRegisterActions,
  onCancel,
  onClose,
}) => {
  const toast = useToast();
  const progress = useProgressAlert();
  const { allCurrencies } = useContaInternacionalBalances();
  const { currencies: currencyOpenings } = useContaInternacionalCurrencies();
  const [step, setStep] = useState(0);
  const currencyOptions = useMemo(
    () =>
      allCurrencies?.length
        ? allCurrencies
        : currencyOpenings.map((row) => ({
            id: row.id,
            label: row.label,
            code: row.code,
            balance: 0,
          })),
    [allCurrencies, currencyOpenings],
  );
  const defaultId = currencyOptions[0]?.id ?? DEFAULT_CURRENCY_ID;
  const [selectedCurrencyId, setSelectedCurrencyId] = useState(currencyId ?? defaultId);
  const currency =
    currencyOptions.find((row) => row.id === selectedCurrencyId) ||
    getCurrency(selectedCurrencyId);
  const liveRails = getCurrencyRails(currencyOpenings, selectedCurrencyId);
  const transferMethods = useMemo(() => liveRails, [liveRails]);
  const [selectedMethodId, setSelectedMethodId] = useState("");
  const [amount, setAmount] = useState("");
  const [beneficiaryFields, setBeneficiaryFields] = useState(EMPTY_BENEFICIARY_FIELDS);

  const railFields = useMemo(
    () => getTransferBeneficiaryFields(selectedMethodId),
    [selectedMethodId]
  );

  const resetState = useCallback(() => {
    setStep(0);
    setSelectedCurrencyId(currencyId ?? defaultId);
    setAmount("");
    setBeneficiaryFields(EMPTY_BENEFICIARY_FIELDS);
  }, [currencyId, defaultId]);

  useEffect(() => {
    if (!isActive) {
      resetState();
    }
  }, [isActive, resetState]);

  useEffect(() => {
    setSelectedMethodId(transferMethods[0]?.id ?? "");
  }, [transferMethods]);

  useEffect(() => {
    setBeneficiaryFields(EMPTY_BENEFICIARY_FIELDS);
  }, [selectedMethodId]);

  const selectedMethod = transferMethods.find((m) => m.id === selectedMethodId);
  const numericAmount = parseFloat(amount.replace(",", ".")) || 0;
  const transferQuote = getTransferQuote({
    currencyId: currency?.id,
    methodId: selectedMethodId,
    amount: numericAmount,
  });
  const balanceAfter = Math.max(0, (currency?.balance ?? 0) - transferQuote.totalDebit);
  const beneficiarySummaryLines = getTransferBeneficiarySummaryLines(
    selectedMethodId,
    beneficiaryFields
  );

  const handleBeneficiaryFieldChange = (key, value) => {
    setBeneficiaryFields((prev) => ({ ...prev, [key]: value }));
  };

  const validateStepOne = useCallback(() => {
    if (!currency) return false;

    if (!selectedMethod) {
      toast.addToast({
        type: "error",
        title: "Meio de transferência",
        description: "Selecione um meio de transferência disponível.",
      });
      return false;
    }

    if (!numericAmount || numericAmount <= 0) {
      toast.addToast({
        type: "error",
        title: "Valor inválido",
        description: "Informe um valor maior que zero.",
      });
      return false;
    }

    if (transferQuote.totalDebit > currency.balance) {
      toast.addToast({
        type: "error",
        title: "Saldo insuficiente",
        description: `Saldo disponível: ${formatCurrencyAmount(currency.balance, currency.id)} (inclui taxa do meio)`,
      });
      return false;
    }

    return true;
  }, [currency, numericAmount, selectedMethod, toast, transferQuote.totalDebit]);

  const validateStepTwo = useCallback(() => {
    const missingField = railFields.find(
      (field) => field.required && !beneficiaryFields[field.key]?.trim()
    );
    if (missingField) {
      toast.addToast({
        type: "error",
        title: "Dados do beneficiário",
        description: `Preencha o campo "${missingField.label}".`,
      });
      return false;
    }
    return true;
  }, [beneficiaryFields, railFields, toast]);

  const handleSubmit = useCallback(() => {
    if (!currency || !selectedMethod) return;

    if (!validateStepOne() || !validateStepTwo()) return;

    const beneficiaryName = beneficiaryFields.beneficiaryName?.trim() ?? "";
    const amountLabel = formatCurrencyAmount(numericAmount, currency.id);

    const closeModal = onClose ?? onCancel;

    progress
      .runWithProgress(
        async () => {
          try {
            await createIbOutboundTransfer({
              currencyCode: currency.code,
              amountMajor: numericAmount,
              scheme: selectedMethod.id,
              beneficiary: beneficiaryFields,
            });
            return {
              success: true,
              message: "Transferência iniciada",
              description: `${amountLabel} via ${selectedMethod.label} para ${beneficiaryName}`,
            };
          } catch (error) {
            return {
              success: false,
              message: "Falha na transferência",
              description: formatBaasErrorMessage(error),
            };
          }
        },
        {
          loading: "Processando transferência...",
          loadingDescription: `${selectedMethod.label} para ${beneficiaryName}`,
          success: "Transferência iniciada",
          error: "Falha na transferência",
        },
        { beforeProgress: closeModal }
      )
      .catch(() => {});
  }, [
    beneficiaryFields,
    currency,
    numericAmount,
    onCancel,
    onClose,
    progress,
    selectedMethod,
    validateStepOne,
    validateStepTwo,
  ]);

  const handleBack = useCallback(() => {
    setStep((current) => Math.max(0, current - 1));
  }, []);

  const handlePrimary = useCallback(() => {
    if (step === 0) {
      if (validateStepOne()) setStep(1);
      return;
    }
    if (step === 1) {
      if (validateStepTwo()) setStep(2);
      return;
    }
    handleSubmit();
  }, [handleSubmit, step, validateStepOne, validateStepTwo]);

  const primaryLabel = step === FINAL_STEP_INDEX ? "Iniciar transferência" : "Continuar";

  useEffect(() => {
    onRegisterActions?.({
      submit: handlePrimary,
      back: handleBack,
      showBack: step > 0,
      label: primaryLabel,
    });
  }, [handleBack, handlePrimary, onRegisterActions, primaryLabel, step]);

  if (!currency) return null;

  const amountInput = (
    <div className="space-y-1">
      <label htmlFor="transfer-amount" className="block text-xs font-medium text-gray-500">
        Valor
      </label>
      <div className="relative">
        <input
          id="transfer-amount"
          type="text"
          inputMode="decimal"
          placeholder="0,00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="valut-input h-10 w-full rounded-xl pr-16"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">
          {currency.code}
        </span>
      </div>
    </div>
  );

  const currencyOriginSection = !currencyId ? (
    <div>
      <CurrencySelect
        id="transfer-origin-currency"
        label="Moeda de origem"
        value={selectedCurrencyId}
        onChange={setSelectedCurrencyId}
        options={currencyOptions}
      />
      <p className="mt-1.5 text-xs text-gray-400">
        Disponível: {formatCurrencyAmount(currency.balance, currency.id)}
      </p>
    </div>
  ) : (
    <div>
      <p className="mb-1.5 text-xs font-medium text-gray-500">Moeda de origem</p>
      <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/60 px-3 py-2.5">
        <CurrencyIcon asset={currency.id} />
        <div>
          <p className="text-sm font-bold text-brand-dark">{currency.label}</p>
          <p className="text-xs text-gray-500">
            Disponível: {formatCurrencyAmount(currency.balance, currency.id)}
          </p>
        </div>
      </div>
    </div>
  );

  const transferMethodsSection = (
    <div>
      <p className="mb-1.5 text-xs font-medium text-gray-500">Meio de transferência</p>
      {transferMethods.length === 0 ? (
        <p className="rounded-xl border border-gray-100 px-3 py-2 text-xs text-gray-500">
          Nenhum meio de transferência disponível para {currency.code} na sua jurisdição.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {transferMethods.map((method) => {
            const isActiveMethod = method.id === selectedMethodId;
            return (
              <button
                key={method.id}
                type="button"
                title={method.description}
                onClick={() => setSelectedMethodId(method.id)}
                className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                  isActiveMethod
                    ? "border-[#e7a73f] bg-[#fff8ec] text-brand-dark"
                    : "border-gray-200 bg-white text-gray-500 hover:border-[#e7a73f]/40"
                }`}
              >
                {method.label}
              </button>
            );
          })}
        </div>
      )}
      {selectedMethod?.description && (
        <p className="mt-1.5 text-xs text-gray-400">{selectedMethod.description}</p>
      )}
    </div>
  );

  const stepSubtitle = TRANSFER_WIZARD_STEPS[step]?.description ?? "";

  return (
    <div className="space-y-1">
      <ContaInternacionalTransferStepper currentStep={step} />
      {stepSubtitle && (
        <p className="mb-3 text-xs text-gray-500">{stepSubtitle}</p>
      )}

      {step === 0 && (
        <section className="space-y-4">
          {currencyOriginSection}
          {transferMethodsSection}
          {amountInput}
        </section>
      )}

      {step === 1 && (
        <section className="space-y-3">
          <p className="text-xs font-medium text-gray-500">
            Dados do beneficiário ({selectedMethod?.label})
          </p>
          {railFields.length === 0 ? (
            <p className="rounded-xl border border-gray-100 px-3 py-2 text-xs text-gray-500">
              Nenhum campo de beneficiário necessário para este meio.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {railFields.map((field) =>
                renderBeneficiaryField(field, beneficiaryFields, handleBeneficiaryFieldChange)
              )}
            </div>
          )}
        </section>
      )}

      {step === 2 && (
        <section className="space-y-3">
          <ContaInternacionalTransferSummary
            currency={currency}
            selectedMethod={selectedMethod}
            beneficiarySummaryLines={beneficiarySummaryLines}
            transferQuote={transferQuote}
            numericAmount={numericAmount}
            balanceAfter={balanceAfter}
            showActionButton={!hideActionButton && !onRegisterActions}
            onSubmit={handleSubmit}
          />
          <p className="text-xs text-gray-400">
            Ao confirmar, a transferência será enviada para processamento.
          </p>
        </section>
      )}

      {!onRegisterActions && (
        <TransferWizardFooter
          step={step}
          onBack={handleBack}
          onPrimary={handlePrimary}
          primaryLabel={primaryLabel}
        />
      )}
    </div>
  );
};
